import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '@/services/apiService';
import { notificationService } from '@/services/notificationService';
import { dispatchDepositCancelled } from '@/utils/depositCancelled';

export function useCancelPendingDeposit() {
  const { t } = useTranslation();
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const cancelDeposit = useCallback(
    async (transactionId: string): Promise<boolean> => {
      if (!transactionId || cancellingId) return false;
      setCancellingId(transactionId);
      try {
        const response = await apiService.post(
          `/payment/deposit/${encodeURIComponent(transactionId)}/cancel`,
          {},
          true
        );
        if (!response.success) {
          notificationService.error(
            response.message ||
              t('transactions.cancelDepositFailed', 'Could not cancel this deposit. Please try again.')
          );
          return false;
        }
        apiService.clearUserCache();
        const status =
          typeof response.data === 'object' && response.data && 'status' in response.data
            ? String((response.data as { status?: string }).status || 'cancelled')
            : 'cancelled';
        dispatchDepositCancelled({ transactionId, status });
        notificationService.success(t('transactions.cancelDepositSuccess', 'Deposit cancelled.'));
        return true;
      } catch (error) {
        // apiService rejects with a plain `{ message }` object, not an Error.
        const message = (error as { message?: unknown } | null)?.message;
        notificationService.error(
          typeof message === 'string' && message.trim()
            ? message.trim()
            : t('transactions.cancelDepositFailed', 'Could not cancel this deposit. Please try again.')
        );
        return false;
      } finally {
        setCancellingId(null);
      }
    },
    [cancellingId, t]
  );

  return { cancelDeposit, cancellingId };
}
